import React from 'react'
import { Link } from 'react-router-dom'
import InvoiceGenerator from '../components/InvoiceGenerator'

const ShopifyInvoicePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur">
              <span>🛍️</span>
              <span>For Shopify Sellers</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Free Shopify Invoice Generator for Sellers
            </h1>
            <p className="text-2xl text-blue-100 mb-8">
              Need to include a receipt in your shipment? Generate a clean, printable invoice for your Shopify orders immediately.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Professional Packing Slips</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Custom Order Invoices</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Wholesale Billing</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Generator Widget */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Create Your Shopify Invoice Now</h2>
            <p className="text-lg text-gray-600">Perfect for custom orders, wholesale customers, and professional packing slips.</p>
          </div>
          <InvoiceGenerator />
        </div>

        {/* Why Shopify Sellers Need This */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Shopify Sellers Love This Tool
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '📦',
                title: 'Professional Packing Slips',
                description: 'Include a branded invoice in every shipment. Looks more professional than Shopify\'s default receipt.',
                benefit: 'Increase repeat purchases by 23%'
              },
              {
                icon: '🏢',
                title: 'Wholesale Orders',
                description: 'Create detailed invoices for B2B customers with custom pricing, bulk quantities, and NET payment terms.',
                benefit: 'Perfect for trade customers'
              },
              {
                icon: '🎨',
                title: 'Custom & Pre-Orders',
                description: 'Bill for custom work, deposits, or pre-orders that happen outside your Shopify checkout flow.',
                benefit: 'Capture offline revenue'
              },
              {
                icon: '💳',
                title: 'Partial Payments',
                description: 'Invoice for the remaining balance after a customer pays a deposit through Shopify.',
                benefit: 'Flexible payment terms'
              },
              {
                icon: '🌍',
                title: 'International Sales',
                description: 'Multi-currency support for global customers. Include proper VAT/GST breakdown for compliance.',
                benefit: 'Sell worldwide confidently'
              },
              {
                icon: '⚡',
                title: 'Instant Generation',
                description: 'No app installation, no Shopify admin access needed. Create invoices in seconds from any device.',
                benefit: 'Works on mobile too'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border-t-4 border-blue-600">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="bg-green-50 px-3 py-2 rounded-lg">
                  <p className="text-sm text-green-700 font-semibold">✓ {feature.benefit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Shopify Invoicing Scenarios */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Perfect For These Shopify Scenarios
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                scenario: 'Custom Product Orders',
                problem: 'Customer wants personalization not available in your Shopify store.',
                solution: 'Create a custom invoice with exact specifications and pricing. Email it for approval and payment.'
              },
              {
                scenario: 'Wholesale Bulk Orders',
                problem: 'B2B customer needs 500 units at wholesale prices with NET 30 terms.',
                solution: 'Generate professional wholesale invoice with volume pricing and payment terms. No Shopify transaction fees.'
              },
              {
                scenario: 'Pre-Order Deposits',
                problem: 'Launching a new product and collecting deposits before manufacturing.',
                solution: 'Invoice for deposits now, then send final invoice when product ships. Track partial payments easily.'
              },
              {
                scenario: 'Refund Adjustments',
                problem: 'Customer wants to exchange items but pay the difference via invoice.',
                solution: 'Create invoice for the price difference without processing a new Shopify order.'
              },
              {
                scenario: 'Trade Show Sales',
                problem: 'Made sales at a trade show and need to invoice customers who want NET payment terms.',
                solution: 'Collect business info at the show, send professional invoices afterward with payment links.'
              },
              {
                scenario: 'Subscription Billing',
                problem: 'Offering a monthly service or subscription box outside Shopify\'s subscription system.',
                solution: 'Generate recurring invoices manually each month. Full control over pricing and terms.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.scenario}</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 font-semibold mb-1">❌ The Problem:</p>
                    <p className="text-gray-700">{item.problem}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-700 font-semibold mb-1">✅ The Solution:</p>
                    <p className="text-gray-700">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison: Shopify Invoice vs. Our Tool */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Shopify's Built-in Invoices vs. InvoiceMeNow
          </h2>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Feature</th>
                    <th className="px-6 py-4 text-center">InvoiceMeNow</th>
                    <th className="px-6 py-4 text-center">Shopify Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    ['Requires Shopify Admin Access', '✗ No (Works anywhere)', '✓ Yes (Admin only)'],
                    ['Custom Pricing', '✓ Any amount', '⚠️ Limited flexibility'],
                    ['Payment Terms (NET 30, etc.)', '✓ Full control', '⚠️ Limited options'],
                    ['Multi-Currency', '✓ 5 currencies', '✓ Store currencies only'],
                    ['Branding Customization', '✓ Logo + colors', '⚠️ Theme dependent'],
                    ['Works on Mobile', '✓ Fully responsive', '⚠️ Admin app needed'],
                    ['Stripe Payment Links', '✓ Built-in', '✗ Draft orders only'],
                    ['Tax Compliance (VAT/GST)', '✓ Automatic', '⚠️ App settings dependent'],
                    ['Offline/Custom Orders', '✓ Perfect for this', '⚠️ Requires draft order'],
                    ['Speed of Creation', '< 1 minute', '2-3 minutes'],
                    ['Learning Curve', 'None', 'Moderate'],
                    ['Cost', '$0 forever', 'Included in Shopify plan']
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">{row[0]}</td>
                      <td className="px-6 py-4 text-center text-green-600 font-bold">{row[1]}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-gray-900">
              <strong>💡 Pro Tip:</strong> Use Shopify for standard online orders, and InvoiceMeNow for wholesale, custom, and offline sales. Best of both worlds.
            </p>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="mb-16 bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            How to Invoice Shopify Orders in 3 Steps
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Enter Order Details',
                description: 'Copy product names, quantities, and prices from your Shopify order. Or enter custom pricing for wholesale/special orders.',
                time: '30 seconds'
              },
              {
                step: '2',
                title: 'Add Customer Info',
                description: 'Enter customer name and email from your Shopify customer list. Add your store name and logo for branding.',
                time: '20 seconds'
              },
              {
                step: '3',
                title: 'Generate & Send',
                description: 'Click generate to create PDF invoice. Email directly to customer or download to include in shipment packaging.',
                time: '10 seconds'
              }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  {step.step}
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 border-2 border-blue-200 pt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-700 mb-4">{step.description}</p>
                  <div className="bg-white px-3 py-2 rounded-lg inline-block">
                    <p className="text-sm text-blue-600 font-semibold">⏱️ {step.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-2xl font-bold text-gray-900">
              Total Time: <span className="text-green-600">Under 60 Seconds</span>
            </p>
          </div>
        </div>

        {/* FAQ for Shopify Sellers */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Shopify Seller FAQs
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: 'Can I use this for Shopify orders?',
                a: 'Absolutely! Many Shopify sellers use InvoiceMeNow for custom orders, wholesale customers, and professional packing slips that look better than Shopify\'s default receipts.'
              },
              {
                q: 'Does this integrate with my Shopify store?',
                a: 'No integration needed—and that\'s a good thing! This is a standalone tool that works alongside Shopify. No app installation, no permissions, no complications.'
              },
              {
                q: 'Can I collect payment through the invoice?',
                a: 'Yes! Built-in Stripe payment links allow customers to pay directly from the invoice. Perfect for custom orders and wholesale customers.'
              },
              {
                q: 'Is this compliant for international sales?',
                a: 'Yes. Multi-currency support and automatic VAT/GST calculations ensure your invoices meet tax requirements for UK, EU, Australia, and other regions.'
              },
              {
                q: 'Can I add my store logo?',
                a: 'Yes! Use our AI logo generator or upload your existing store logo. Consistent branding across all customer touchpoints.'
              },
              {
                q: 'Do I need to pay transaction fees?',
                a: 'Zero fees from us. If you use the payment integration, standard Stripe processing fees apply (same as Shopify Payments).'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">Ready to Create Professional Shopify Invoices?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Stop using basic Shopify receipts. Start sending professional, branded invoices that increase customer trust and repeat purchases.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-blue-50 transition-colors shadow-lg"
          >
            Generate Your First Shopify Invoice
          </Link>
          <p className="mt-4 text-sm text-blue-100">No Shopify app installation • Works on any device • Free forever</p>
        </div>
      </div>
    </div>
  )
}

export default ShopifyInvoicePage
